import { ControlStateMachine } from '@/core/battle/ControlStateMachine';
import { BattleBus } from '../../events/Events';
import { useBattleStore } from '../../store/useBattleStore';
import { FactionType } from '../types/battle.types';
import { CombatCalculator } from '@/core/battle/CombatCalculator';

export class TooltipManager {
    private element: HTMLElement;
    private displayTimeout: ReturnType<typeof setTimeout> | null = null;
    private mousePos = { x: 0, y: 0 };
    private currentUnitId: string | null = null;
    private readonly HOVER_DELAY = 400;

    // Зберігаємо посилання на функції-обробники для їх коректного видалення
    private storeUnsubscribe: () => void;

    constructor(private stateMachine: ControlStateMachine) {
        this.element = this.initElement();
        // this.setupListeners();

        
        
        // Підписка на Zustand повертає функцію для відписки
        this.storeUnsubscribe = useBattleStore.subscribe(
            (state) => state.selectedSkillId,
            (selectedSkillId) => this.handleSelectionChange(selectedSkillId)
        );
    }

    private setupListeners() {
        this.stateMachine.onStateChange((from, to) => {
            this.hide();
        });
        BattleBus.on('UNIT_HOVER', this.handleUnitHover);
        BattleBus.on('UNIT_MOUSEMOVE', this.handleMouseMove);
        BattleBus.on('UNIT_OUT', this.handleUnitOut);
        BattleBus.on('TOOLTIP_UPDATE', this.handleTooltipUpdate);
    }

    // Окремі методи для обробки подій (простіше видаляти)
    private handleUnitHover = ({ id, position }: { id: string, position: { x: number, y: number } }) => {
        this.currentUnitId = id;
        this.mousePos = position;
        this.startIdlingTimer();
    };

    private handleMouseMove = ({ position }: { position: { x: number, y: number } }) => {
        this.mousePos = position;
        if (this.element.style.display === 'block') {
            this.updatePosition();
        } else {
            this.startIdlingTimer();
        }
    };

    private handleUnitOut = () => {
        this.currentUnitId = null;
        this.clearTimer();
        this.hide();
    };

    private handleTooltipUpdate = () => {
        
        if (this.currentUnitId) {
            const unit = useBattleStore.getState().units.get(this.currentUnitId);
            if (unit?.isDead) {
                this.currentUnitId = null;
                this.hide();
            } else {
                this.render();
            }
        }
    };

    private handleSelectionChange(skillId: string | null) {
        if (this.element.style.display === 'block' && skillId) {
            this.handleUnitOut();
        }
    }

    private initElement(): HTMLElement {
        let el = document.getElementById('game-tooltip');
        if (!el) {
            el = document.createElement('div');
            el.id = 'game-tooltip';
            document.body.appendChild(el);
        }
        return el;
    }

    private startIdlingTimer() {
        this.clearTimer();
        this.displayTimeout = setTimeout(() => {
            if (this.currentUnitId) {
                const { selectedSkillId, units } = useBattleStore.getState()
                const unit = units.get(this.currentUnitId);
                if (!selectedSkillId && unit) this.show();
            }
        }, this.HOVER_DELAY);
    }

    private show() {
        this.render(); // Спершу наповнюємо
        this.element.style.display = 'block';
        this.updatePosition();
    }

    private updatePosition() {
        // Зміщення, щоб тултіп не був прямо під курсором
        this.element.style.left = `${this.mousePos.x + 15}px`;
        this.element.style.top = `${this.mousePos.y + 15}px`;
    }

    private hide() {
        this.element.style.display = 'none';
    }

    private clearTimer() {
        if (this.displayTimeout) {
            clearTimeout(this.displayTimeout);
            this.displayTimeout = null;
        }
    }

    private render() {
        if (!this.currentUnitId) return;
        
        // Беремо дані зі стору, а не з реєстру візуальних об'єктів
        const { units, selectedUnitId, activeFaction } = useBattleStore.getState();
        
        const unitData = units.get(this.currentUnitId);
        
        if (!unitData) return;

        const isFriendly = unitData.faction === FactionType.Player;
        const isSelected = selectedUnitId === unitData.unitId;
        const totalHP = unitData.stats.hp * unitData.count;
        const count = CombatCalculator.getEffectiveCount(unitData.count);
        
        const currentTotalHP = ((unitData.count - 1) * unitData.stats.hp) + unitData.currentUnitHp
        this.element.innerHTML = `
            <div class="tooltip-title ${isFriendly ? 'friend' : 'enemy'}">${unitData.unitName}</div>
            <div class="tooltip-stat">Total HP: <span clas="tooltip-value">${currentTotalHP} / ${totalHP}</span></div>
            <div class="tooltip-stat">Stamina: <span clas="tooltip-value">${unitData.currentStamina} / ${unitData.stats.stamina}</span></div>
            <div class="tooltip-stat">Hit Chance: <span clas="tooltip-value">${Math.max(unitData.stats.meleeSkill || 0, unitData.stats.rangedSkill || 0)}</span></div>
            <div class="tooltip-stat">Damage: <span clas="tooltip-value">${(unitData.stats.damageMin * count).toFixed(0)} - ${(unitData.stats.damageMax * count).toFixed(0)}</span></div>
            <div class="tooltip-stat">Armor Pen: <span clas="tooltip-value">${unitData.stats.armorPenetration * 100}%</span></div>
            <div class="tooltip-stat">Armor: <span clas="tooltip-value">${unitData.stats.armor}</span></div>
            
            <div class="tooltip-stat">Speed: <span clas="tooltip-value">${unitData.stats.speed}</span></div>
            <div class="tooltip-stat">Evasion: <span clas="tooltip-value">${unitData.stats.evasion}</span></div>
            <div class="tooltip-footer">pos: ${unitData.q}:${unitData.r}</div>
            
        `;
    }

    /**
     * ПОВНЕ ОЧИЩЕННЯ (Викликати в BattleScene.destroy)
     */
    public destroy() {
        this.clearTimer();
        
        // 1. Відписуємось від шини подій
        BattleBus.off('UNIT_HOVER', this.handleUnitHover);
        BattleBus.off('UNIT_MOUSEMOVE', this.handleMouseMove);
        BattleBus.off('UNIT_OUT', this.handleUnitOut);
        BattleBus.off('TOOLTIP_UPDATE', this.handleTooltipUpdate);

        // 2. Відписуємось від стору
        // if (this.storeUnsubscribe) this.storeUnsubscribe();

        // 3. Видаляємо елемент з DOM
        if (this.element && this.element.parentNode) {
            this.element.parentNode.removeChild(this.element);
        }
        
        console.log("TooltipManager: Cleaned up.");
    }
}